import React, { useState, useEffect } from 'react';
import { useParentPortal } from '../contexts/ParentPortalContext';
import { supabase } from '../../../../lib/supabase';
import {
  TrendingUp,
  Brain,
  BarChart3,
  Target,
  Users,
  Calendar,
  Award,
  AlertCircle,
  ChevronRight,
  Sparkles,
  ArrowUp,
  ArrowDown,
  Minus,
  BookOpen,
  GraduationCap,
  Clock,
  DollarSign,
  FileText,
  CheckCircle,
  XCircle,
  Activity,
  Zap,
  Shield,
  Star
} from 'lucide-react';

const InsightsSection = () => {
  const { selectedStudent, studentJourneyPhase, studentProgress } = useParentPortal();

  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('30days');
  const [progressData, setProgressData] = useState(null);

  useEffect(() => {
    if (selectedStudent) {
      fetchInsights();
      fetchProgressData();
    }
  }, [selectedStudent, timeRange]);

  const fetchInsights = async () => {
    if (!selectedStudent) return;

    setLoading(true);
    try {
      // Fetch student's assessment data
      const { data: assessmentData } = await supabase
        .from('student_assessments')
        .select('*')
        .eq('student_id', selectedStudent.id)
        .single();

      // Fetch student's colleges
      const { data: studentColleges } = await supabase
        .from('colleges')
        .select('*')
        .eq('student_id', selectedStudent.id);

      // Fetch essays
      const { data: essays } = await supabase
        .from('essays')
        .select('*')
        .eq('student_id', selectedStudent.id);

      // Generate insights based on data
      const generatedInsights = generateInsights({
        assessment: assessmentData,
        colleges: studentColleges || [],
        essays: essays || [],
        progress: studentProgress
      });

      setInsights(generatedInsights);
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgressData = async () => {
    if (!selectedStudent) return;

    try {
      // Calculate progress over time
      const endDate = new Date();
      const startDate = new Date();

      switch(timeRange) {
        case '7days':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30days':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '90days':
          startDate.setDate(startDate.getDate() - 90);
          break;
        default:
          startDate.setDate(startDate.getDate() - 30);
      }

      // Mock progress data for demonstration
      const data = {
        milestones: {
          completed: studentProgress?.completedMilestones || 0,
          total: studentProgress?.totalMilestones || 20,
          weeklyProgress: 3,
          trend: 'up'
        },
        colleges: {
          total: studentProgress?.colleges?.length || 0,
          reachCount: studentProgress?.colleges?.filter(c => c.category === 'reach')?.length || 0,
          targetCount: studentProgress?.colleges?.filter(c => c.category === 'target')?.length || 0,
          safetyCount: studentProgress?.colleges?.filter(c => c.category === 'safety')?.length || 0,
          weeklyAdded: 2,
          trend: 'up'
        },
        essays: {
          total: studentProgress?.essays?.length || 0,
          completed: studentProgress?.essays?.filter(e => e.status === 'complete')?.length || 0,
          inProgress: studentProgress?.essays?.filter(e => e.status === 'in_progress')?.length || 0,
          weeklyProgress: 1,
          trend: 'stable'
        },
        engagement: {
          loginStreak: 5,
          weeklyActivity: 12,
          parentEngagement: 8,
          trend: 'up'
        }
      };

      setProgressData(data);
    } catch (error) {
      console.error('Error fetching progress data:', error);
    }
  };

  const generateInsights = ({ assessment, colleges, essays, progress }) => {
    const insights = {
      strengths: [],
      opportunities: [],
      risks: [],
      recommendations: []
    };

    // Analyze strengths
    if (assessment) {
      insights.strengths.push({
        title: 'Strong Academic Profile',
        description: 'GPA and test scores position well for target schools',
        impact: 'high',
        icon: Award
      });
    }

    if (colleges.length >= 10) {
      insights.strengths.push({
        title: 'Comprehensive College List',
        description: 'Well-developed list with good balance of reach, target, and safety schools',
        impact: 'high',
        icon: GraduationCap
      });
    }

    if (essays.length >= 3) {
      insights.strengths.push({
        title: 'Essay Progress',
        description: 'Multiple essays in development showing good momentum',
        impact: 'medium',
        icon: FileText
      });
    }

    // Identify opportunities
    if (!assessment) {
      insights.opportunities.push({
        title: 'Complete Strategic Assessment',
        description: 'Unlock personalized recommendations and strategic positioning insights',
        action: 'Start Assessment',
        priority: 'high',
        icon: Brain
      });
    }

    if (colleges.length < 5) {
      insights.opportunities.push({
        title: 'Expand College Research',
        description: 'Add more schools to create a balanced list with 8-12 colleges',
        action: 'Research Colleges',
        priority: 'high',
        icon: Target
      });
    }

    const safetyCount = colleges.filter(c => c.category === 'safety').length;
    if (safetyCount < 2) {
      insights.opportunities.push({
        title: 'Add Safety Schools',
        description: 'Include 2-3 safety schools to ensure admission options',
        action: 'Find Safety Schools',
        priority: 'medium',
        icon: Shield
      });
    }

    // Identify risks
    const deadlinesApproaching = colleges.filter(c => {
      if (!c.deadline) return false;
      const daysUntil = Math.ceil((new Date(c.deadline) - new Date()) / (1000 * 60 * 60 * 24));
      return daysUntil <= 30 && daysUntil > 0;
    }).length;

    if (deadlinesApproaching > 0) {
      insights.risks.push({
        title: `${deadlinesApproaching} Deadline${deadlinesApproaching > 1 ? 's' : ''} Approaching`,
        description: 'Applications due within 30 days need immediate attention',
        severity: 'high',
        icon: Clock
      });
    }

    if (essays.length === 0 && colleges.length > 5) {
      insights.risks.push({
        title: 'Essay Development Needed',
        description: 'College list is growing but essays haven\'t been started',
        severity: 'medium',
        icon: AlertCircle
      });
    }

    // Generate recommendations
    insights.recommendations.push({
      title: 'Weekly Parent Check-in',
      description: 'Schedule regular discussions about progress and challenges',
      category: 'engagement',
      icon: Users
    });

    if (progress?.completedMilestones < 5) {
      insights.recommendations.push({
        title: 'Focus on Foundation',
        description: 'Complete core milestones like assessment and initial college research',
        category: 'strategy',
        icon: Target
      });
    }

    insights.recommendations.push({
      title: 'Financial Aid Planning',
      description: 'Start FAFSA and CSS Profile preparation early',
      category: 'financial',
      icon: DollarSign
    });

    return insights;
  };

  const getProgressTrendIcon = (trend) => {
    switch(trend) {
      case 'up':
        return <ArrowUp className="w-4 h-4 text-emerald-600" />;
      case 'down':
        return <ArrowDown className="w-4 h-4 text-coral-600" />;
      default:
        return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  const getImpactColor = (impact) => {
    switch(impact) {
      case 'high':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'medium':
        return 'bg-azure-100 text-azure-700 border-azure-200';
      case 'low':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'high':
        return 'bg-coral-100 text-coral-700 border-coral-200';
      case 'medium':
        return 'bg-gold-100 text-gold-700 border-gold-200';
      case 'low':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  if (!selectedStudent) {
    return (
      <div className="text-center py-12">
        <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          No Student Selected
        </h2>
        <p className="text-slate-600">
          Select a student to view AI-powered insights and strategic analysis.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-1/3"></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-48 bg-slate-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Strategic Insights</h1>
          <p className="text-slate-600 mt-1">
            AI-powered analysis and strategic recommendations for {selectedStudent.name}'s college journey
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-azure-500"
          >
            <option value="7days">Last 7 days</option>
            <option value="30days">Last 30 days</option>
            <option value="90days">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'progress', label: 'Progress Analytics', icon: TrendingUp },
          { id: 'strategic', label: 'Strategic Position', icon: Target },
          { id: 'recommendations', label: 'Recommendations', icon: Sparkles }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="text-sm font-medium">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-teal-600" />
                </div>
                {progressData && getProgressTrendIcon(progressData.milestones.trend)}
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {progressData?.milestones.completed || 0}/{progressData?.milestones.total || 20}
              </div>
              <div className="text-sm text-slate-600">Milestones Complete</div>
              {progressData?.milestones.weeklyProgress > 0 && (
                <div className="text-xs text-emerald-600 mt-1">
                  +{progressData.milestones.weeklyProgress} this week
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 bg-azure-50 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-azure-600" />
                </div>
                {progressData && getProgressTrendIcon(progressData.colleges.trend)}
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {progressData?.colleges.total || 0}
              </div>
              <div className="text-sm text-slate-600">Colleges Listed</div>
              {progressData?.colleges.weeklyAdded > 0 && (
                <div className="text-xs text-emerald-600 mt-1">
                  +{progressData.colleges.weeklyAdded} this week
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-violet-600" />
                </div>
                {progressData && getProgressTrendIcon(progressData.essays.trend)}
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {progressData?.essays.completed || 0}/{progressData?.essays.total || 0}
              </div>
              <div className="text-sm text-slate-600">Essays Complete</div>
              {progressData?.essays.inProgress > 0 && (
                <div className="text-xs text-azure-600 mt-1">
                  {progressData.essays.inProgress} in progress
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-emerald-600" />
                </div>
                {progressData && getProgressTrendIcon(progressData.engagement.trend)}
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {progressData?.engagement.loginStreak || 0}
              </div>
              <div className="text-sm text-slate-600">Day Streak</div>
              <div className="text-xs text-emerald-600 mt-1">
                {progressData?.engagement.weeklyActivity || 0} activities this week
              </div>
            </div>
          </div>

          {/* Insights Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Strengths */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Star className="w-5 h-5 text-emerald-600" />
                <h3 className="text-lg font-semibold text-slate-900">Key Strengths</h3>
              </div>
              <div className="space-y-3">
                {insights?.strengths.length > 0 ? (
                  insights.strengths.map((strength, index) => (
                    <div key={index} className="flex gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                        <strength.icon className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-emerald-900">{strength.title}</h4>
                        <p className="text-sm text-emerald-700 mt-1">{strength.description}</p>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full mt-2 ${getImpactColor(strength.impact)}`}>
                          {strength.impact} impact
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-sm">Building strengths through progress...</p>
                )}
              </div>
            </div>

            {/* Risks */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="w-5 h-5 text-coral-600" />
                <h3 className="text-lg font-semibold text-slate-900">Risk Factors</h3>
              </div>
              <div className="space-y-3">
                {insights?.risks.length > 0 ? (
                  insights.risks.map((risk, index) => (
                    <div key={index} className="flex gap-3 p-3 bg-coral-50 rounded-lg border border-coral-200">
                      <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                        <risk.icon className="w-4 h-4 text-coral-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-coral-900">{risk.title}</h4>
                        <p className="text-sm text-coral-700 mt-1">{risk.description}</p>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full mt-2 ${getSeverityColor(risk.severity)}`}>
                          {risk.severity} severity
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <p className="text-emerald-700 text-sm font-medium">No immediate risks identified</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Opportunities */}
          <div className="bg-gradient-to-r from-azure-50 to-indigo-50 rounded-xl border border-azure-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-5 h-5 text-azure-600" />
              <h3 className="text-lg font-semibold text-slate-900">Strategic Opportunities</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {insights?.opportunities.map((opportunity, index) => (
                <div key={index} className="bg-white rounded-lg p-4 border border-azure-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-azure-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <opportunity.icon className="w-5 h-5 text-azure-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900">{opportunity.title}</h4>
                      <p className="text-sm text-slate-600 mt-1">{opportunity.description}</p>
                      <button className="mt-3 px-3 py-1.5 bg-azure-500 text-white text-sm rounded-lg hover:bg-azure-600 transition-colors flex items-center gap-1">
                        {opportunity.action}
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Progress Analytics Tab */}
      {activeTab === 'progress' && (
        <div className="space-y-6">
          {/* Journey Progress */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Journey Progress Timeline</h3>
            <div className="space-y-4">
              {/* Overall Progress Bar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Overall Completion</span>
                  <span className="text-sm font-bold text-slate-900">
                    {Math.round((progressData?.milestones.completed / progressData?.milestones.total) * 100)}%
                  </span>
                </div>
                <div className="w-full h-3 bg-stone-200 rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all duration-500"
                    style={{
                      width: `${(progressData?.milestones.completed / progressData?.milestones.total) * 100}%`,
                      background: 'linear-gradient(90deg, #26a69a 0%, #4290f5 50%, #643eef 100%)'
                    }}
                  />
                </div>
              </div>

              {/* Category Progress */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <h4 className="font-medium text-teal-900 mb-2">Foundation</h4>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-teal-200 rounded-full overflow-hidden">
                      <div className="h-full bg-teal-500" style={{ width: '75%' }} />
                    </div>
                    <span className="text-sm font-medium text-teal-700">75%</span>
                  </div>
                  <p className="text-xs text-teal-600 mt-2">Assessment, Profile, Goals</p>
                </div>

                <div className="p-4 bg-azure-50 rounded-lg border border-azure-200">
                  <h4 className="font-medium text-azure-900 mb-2">Research</h4>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-azure-200 rounded-full overflow-hidden">
                      <div className="h-full bg-azure-500" style={{ width: '60%' }} />
                    </div>
                    <span className="text-sm font-medium text-azure-700">60%</span>
                  </div>
                  <p className="text-xs text-azure-600 mt-2">College List, Visits, Requirements</p>
                </div>

                <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                  <h4 className="font-medium text-violet-900 mb-2">Applications</h4>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-violet-200 rounded-full overflow-hidden">
                      <div className="h-full bg-violet-500" style={{ width: '25%' }} />
                    </div>
                    <span className="text-sm font-medium text-violet-700">25%</span>
                  </div>
                  <p className="text-xs text-violet-600 mt-2">Essays, Forms, Submissions</p>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Heatmap */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Activity Patterns</h3>
            <div className="grid grid-cols-7 gap-1">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <div key={index} className="text-center">
                  <div className="text-xs text-slate-600 mb-2">{day}</div>
                  <div className="space-y-1">
                    {[1, 2, 3, 4].map((week) => {
                      const intensity = Math.random();
                      return (
                        <div
                          key={week}
                          className="w-full h-8 rounded"
                          style={{
                            backgroundColor: intensity > 0.7 ? '#26a69a' :
                                           intensity > 0.4 ? '#80cbc4' :
                                           intensity > 0.1 ? '#e0f2f1' : '#f5f5f5',
                            opacity: intensity > 0.1 ? 1 : 0.3
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 mt-4 text-xs text-slate-600">
              <span>Less</span>
              <div className="flex gap-1">
                <div className="w-4 h-4 bg-stone-200 rounded"></div>
                <div className="w-4 h-4 bg-teal-200 rounded"></div>
                <div className="w-4 h-4 bg-teal-400 rounded"></div>
                <div className="w-4 h-4 bg-teal-600 rounded"></div>
              </div>
              <span>More</span>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Recent Activities</h3>
            <div className="space-y-3">
              {[
                { action: 'Completed college research', time: '2 hours ago', icon: GraduationCap, color: 'azure' },
                { action: 'Submitted essay draft', time: '1 day ago', icon: FileText, color: 'violet' },
                { action: 'Updated academic profile', time: '3 days ago', icon: Award, color: 'emerald' },
                { action: 'Added 3 colleges to list', time: '5 days ago', icon: Target, color: 'teal' },
              ].map((activity, index) => (
                <div key={index} className="flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors">
                  <div className={`w-8 h-8 bg-${activity.color}-100 rounded-lg flex items-center justify-center`}>
                    <activity.icon className={`w-4 h-4 text-${activity.color}-600`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">{activity.action}</p>
                    <p className="text-xs text-slate-500">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Strategic Position Tab */}
      {activeTab === 'strategic' && (
        <div className="space-y-6">
          {/* Competitive Position */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Competitive Positioning</h3>

            {/* Academic Standing */}
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Academic Standing</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-emerald-700">GPA</span>
                      <span className="font-bold text-emerald-900">3.85</span>
                    </div>
                    <div className="text-xs text-emerald-600">Top 15% of class</div>
                  </div>
                  <div className="p-3 bg-azure-50 rounded-lg border border-azure-200">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-azure-700">SAT</span>
                      <span className="font-bold text-azure-900">1420</span>
                    </div>
                    <div className="text-xs text-azure-600">91st percentile</div>
                  </div>
                  <div className="p-3 bg-violet-50 rounded-lg border border-violet-200">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-violet-700">AP Courses</span>
                      <span className="font-bold text-violet-900">8</span>
                    </div>
                    <div className="text-xs text-violet-600">Strong rigor</div>
                  </div>
                </div>
              </div>

              {/* College Fit Analysis */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">College Fit Analysis</h4>
                <div className="space-y-2">
                  {progressData?.colleges.total > 0 && (
                    <>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-coral-500 rounded-full"></div>
                          <span className="text-sm text-slate-700">Reach Schools</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{progressData.colleges.reachCount}</span>
                          <span className="text-xs text-slate-500">(25-40% chance)</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-azure-500 rounded-full"></div>
                          <span className="text-sm text-slate-700">Target Schools</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{progressData.colleges.targetCount}</span>
                          <span className="text-xs text-slate-500">(40-60% chance)</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                          <span className="text-sm text-slate-700">Safety Schools</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{progressData.colleges.safetyCount}</span>
                          <span className="text-xs text-slate-500">(60%+ chance)</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Differentiators */}
          <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl border border-violet-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Star className="w-5 h-5 text-violet-600" />
              <h3 className="text-lg font-semibold text-slate-900">Key Differentiators</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white/70 rounded-lg p-4 border border-violet-200">
                <h4 className="font-medium text-violet-900 mb-2">Leadership Experience</h4>
                <p className="text-sm text-violet-700">
                  Student council president and founder of environmental club
                </p>
              </div>
              <div className="bg-white/70 rounded-lg p-4 border border-violet-200">
                <h4 className="font-medium text-violet-900 mb-2">Unique Perspective</h4>
                <p className="text-sm text-violet-700">
                  First-generation college student with international background
                </p>
              </div>
              <div className="bg-white/70 rounded-lg p-4 border border-violet-200">
                <h4 className="font-medium text-violet-900 mb-2">Community Impact</h4>
                <p className="text-sm text-violet-700">
                  200+ volunteer hours in local literacy programs
                </p>
              </div>
              <div className="bg-white/70 rounded-lg p-4 border border-violet-200">
                <h4 className="font-medium text-violet-900 mb-2">Academic Passion</h4>
                <p className="text-sm text-violet-700">
                  Published research in youth science journal
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations Tab */}
      {activeTab === 'recommendations' && (
        <div className="space-y-6">
          {/* AI Recommendations */}
          <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl border border-indigo-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Brain className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-slate-900">AI-Powered Recommendations</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {insights?.recommendations.map((rec, index) => (
                <div key={index} className="bg-white rounded-lg p-4 border border-indigo-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <rec.icon className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">{rec.title}</h4>
                      <p className="text-sm text-slate-600 mt-1">{rec.description}</p>
                      <span className="inline-block px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full mt-2">
                        {rec.category}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Plan */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Recommended Action Plan</h3>
            <div className="space-y-3">
              {[
                {
                  phase: 'This Week',
                  actions: [
                    'Complete strategic assessment if not done',
                    'Research 2-3 additional safety schools',
                    'Start brainstorming essay topics'
                  ],
                  urgent: true
                },
                {
                  phase: 'Next 2 Weeks',
                  actions: [
                    'Schedule college visits for top choices',
                    'Begin first essay draft',
                    'Meet with school counselor'
                  ],
                  urgent: false
                },
                {
                  phase: 'This Month',
                  actions: [
                    'Complete college list with 10-12 schools',
                    'Finish common app essay',
                    'Start FAFSA preparation'
                  ],
                  urgent: false
                },
              ].map((plan, index) => (
                <div key={index} className={`p-4 rounded-lg border ${
                  plan.urgent ? 'bg-coral-50 border-coral-200' : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`font-medium ${
                      plan.urgent ? 'text-coral-900' : 'text-slate-900'
                    }`}>{plan.phase}</h4>
                    {plan.urgent && (
                      <span className="px-2 py-1 bg-coral-100 text-coral-700 text-xs rounded-full">Urgent</span>
                    )}
                  </div>
                  <ul className="space-y-1">
                    {plan.actions.map((action, actionIndex) => (
                      <li key={actionIndex} className={`flex items-start gap-2 text-sm ${
                        plan.urgent ? 'text-coral-700' : 'text-slate-600'
                      }`}>
                        <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Parent Support Tips */}
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-5 h-5 text-emerald-600" />
              <h3 className="text-lg font-semibold text-emerald-900">How You Can Help</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                'Review essay drafts with constructive feedback',
                'Help research financial aid opportunities',
                'Arrange college visits and virtual tours',
                'Celebrate small victories along the way',
                'Maintain open communication about stress',
                'Support balanced approach to applications'
              ].map((tip, index) => (
                <div key={index} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-emerald-800">{tip}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightsSection;